import { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Database, Table, X, ChevronRight, ChevronDown } from "lucide-react";
import React from "react";
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  MarkerType,
  NodeTypes,
  Position,
  Handle,
  ReactFlowInstance,
} from "reactflow";
import dagre from "dagre";
import "reactflow/dist/style.css";
import type { Dataset } from "../types";
import { useMobile } from "../hooks/useMobile";

interface SchemaDocumentationModalProps {
  datasets: Dataset[];
  isOpen: boolean;
  onClose: () => void;
  relations?: Array<[string, string]>;
  initialTableName?: string;
}

// Константы стилей для графа
const EDGE_STYLE = {
  type: "bezier" as const,
  animated: false,
  style: {
    stroke: "#9ca3af", // Серый цвет по умолчанию
    strokeWidth: 2,
  }
};

// Кастомный узел для таблицы (без колонок)
const TableNode = ({ data, id }: { data: { label: string; description?: string; isExpanded?: boolean; isSelected?: boolean; isDependent?: boolean; onExpandClick?: (nodeId: string) => void }; id: string }) => {
  const getBorderColor = () => {
    if (data.isSelected) return 'border-primary border-4';
    if (data.isDependent) return 'border-blue-400 border-2';
    return 'border-primary/60 border-2';
  };

  const getBackgroundColor = () => {
    if (data.isSelected) return 'bg-blue-50';
    if (data.isDependent) return 'bg-blue-50/50';
    return 'bg-white';
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onExpandClick) {
      data.onExpandClick(id);
    }
  };

  return (
    <div className="relative">
      <div className={`${getBackgroundColor()} ${getBorderColor()} rounded-lg shadow-lg pl-4 pr-2 py-3 min-w-[200px] w-fit hover:shadow-xl transition-all relative flex items-center gap-2`}>
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: "#3b82f6", width: 8, height: 8 }}
        />
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: "#3b82f6", width: 8, height: 8 }}
        />
        <Table className="w-4 h-4 text-primary flex-shrink-0" />
        <h3 className="font-mono font-semibold text-sm text-neutral-text whitespace-nowrap">
          {data.label}
        </h3>
        <button
          onClick={handleExpandClick}
          className="flex-shrink-0 p-2 rounded hover:bg-primary/10 transition-colors text-primary hover:text-primary/80 ml-auto"
          title="Раскрыть описание"
        >
          {data.isExpanded ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

// Функция для автоматического размещения узлов с помощью dagre
const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  if (nodes.length === 0) {
    return { nodes, edges };
  }

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: "LR", // Слева направо
    nodesep: 100, // Расстояние между узлами
    ranksep: 50, // Расстояние между уровнями (уменьшено для компактности)
    align: "UL", // Выравнивание
  });

  nodes.forEach((node) => {
    // Вычисляем ширину узла на основе длины названия
    const label = node.data?.label || '';
    const estimatedWidth = Math.max(200, label.length * 8 + 80); // Примерная ширина: 8px на символ + отступы
    dagreGraph.setNode(node.id, { width: estimatedWidth, height: 80 });
  });

  edges.forEach((edge) => {
    if (edge.source && edge.target) {
      dagreGraph.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      position: {
        x: nodeWithPosition.x - 100, // Центрируем узел
        y: nodeWithPosition.y - 40,
      },
    };
  });

  // Сохраняем edges с обновленными стилями
  const layoutedEdges = edges.map((edge) => ({
    ...edge,
    id: edge.id || `edge-${edge.source}-${edge.target}`,
    ...EDGE_STYLE,
  }));

  return { nodes: layoutedNodes, edges: layoutedEdges };
};

const nodeTypes: NodeTypes = {
  table: TableNode,
};

export const SchemaDocumentationModal = ({ datasets, isOpen, onClose, relations: yamlRelations, initialTableName }: SchemaDocumentationModalProps) => {
  const [selectedTable, setSelectedTable] = useState<Dataset | null>(null);
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hasCentered, setHasCentered] = useState(false);

  // Создаем базовые узлы для React Flow (без позиций)
  // Не включаем expandedNodeId в зависимости, чтобы layout не пересчитывался при раскрытии
  const baseNodes = useMemo<Node[]>(() => {
    return datasets
      .filter((ds) => ds.name)
      .map((dataset) => {
        return {
          id: dataset.name!,
          type: "table",
          position: { x: 0, y: 0 }, // Позиции будут вычислены dagre
          data: {
            label: dataset.name,
            description: dataset.description,
            dataset: dataset,
          },
        };
      });
  }, [datasets]);

  // Создаем рёбра для React Flow
  const baseEdges = useMemo<Edge[]>(() => {
    if (!yamlRelations || yamlRelations.length === 0) return [];

    const edges: Edge[] = [];
    const edgeIds = new Set<string>();

    yamlRelations.forEach((rel) => {
      const [fromTable, toTable] = rel;
      const fromExists = datasets.some((ds) => ds.name === fromTable);
      const toExists = datasets.some((ds) => ds.name === toTable);
      
      if (fromExists && toExists) {
        const edgeId = `edge-${fromTable}-${toTable}`;
        // Избегаем дубликатов
        if (!edgeIds.has(edgeId)) {
          edgeIds.add(edgeId);
          edges.push({
            id: edgeId,
            source: fromTable,
            target: toTable,
            ...EDGE_STYLE,
          });
        }
      }
    });

    return edges;
  }, [yamlRelations, datasets]);

  // Вычисляем layout с помощью dagre
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    if (baseNodes.length === 0) {
      return { nodes: baseNodes, edges: baseEdges };
    }
    if (baseEdges.length === 0) {
      // Если нет edges, размещаем узлы в сетке
      const gridNodes = baseNodes.map((node, index) => {
        const cols = Math.ceil(Math.sqrt(baseNodes.length));
        const row = Math.floor(index / cols);
        const col = index % cols;
        return {
          ...node,
          position: { x: col * 250, y: row * 150 },
        };
      });
      return { nodes: gridNodes, edges: baseEdges };
    }
    return getLayoutedElements(baseNodes, baseEdges);
  }, [baseNodes, baseEdges]);

  // Находим всю цепочку зависимых узлов в обе стороны (BFS обход графа)
  const dependentNodeIds = useMemo(() => {
    if (!selectedNodeId) return new Set<string>();
    const dependent = new Set<string>();
    
    // Создаем граф связей для быстрого поиска (forward)
    const forwardGraph = new Map<string, string[]>();
    // Создаем обратный граф для поиска зависимостей (backward)
    const backwardGraph = new Map<string, string[]>();
    
    baseEdges.forEach((edge) => {
      // Forward graph: source -> target
      if (!forwardGraph.has(edge.source)) {
        forwardGraph.set(edge.source, []);
      }
      forwardGraph.get(edge.source)!.push(edge.target);
      
      // Backward graph: target -> source
      if (!backwardGraph.has(edge.target)) {
        backwardGraph.set(edge.target, []);
      }
      backwardGraph.get(edge.target)!.push(edge.source);
    });

    // BFS обход вперед (находим все узлы, на которые влияет выбранный)
    const forwardVisited = new Set<string>();
    const forwardQueue: string[] = [selectedNodeId];
    forwardVisited.add(selectedNodeId);

    while (forwardQueue.length > 0) {
      const currentNode = forwardQueue.shift()!;
      const neighbors = forwardGraph.get(currentNode) || [];
      for (const neighbor of neighbors) {
        if (!forwardVisited.has(neighbor)) {
          forwardVisited.add(neighbor);
          dependent.add(neighbor);
          forwardQueue.push(neighbor);
        }
      }
    }

    // BFS обход назад (находим все узлы, которые влияют на выбранный)
    const backwardVisited = new Set<string>();
    const backwardQueue: string[] = [selectedNodeId];
    backwardVisited.add(selectedNodeId);

    while (backwardQueue.length > 0) {
      const currentNode = backwardQueue.shift()!;
      const neighbors = backwardGraph.get(currentNode) || [];
      for (const neighbor of neighbors) {
        if (!backwardVisited.has(neighbor)) {
          backwardVisited.add(neighbor);
          dependent.add(neighbor);
          backwardQueue.push(neighbor);
        }
      }
    }

    return dependent;
  }, [selectedNodeId, baseEdges]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Обработчик клика на кнопку раскрытия
  const handleExpandClick = useCallback((nodeId: string) => {
    const dataset = datasets.find((ds) => ds.name === nodeId);
    if (dataset) {
      if (expandedNodeId === nodeId) {
        setExpandedNodeId(null);
        setSelectedTable(null);
      } else {
        setExpandedNodeId(nodeId);
        setSelectedTable(dataset);
      }
    }
  }, [expandedNodeId, datasets]);

  // Обновляем узлы и рёбра при изменении layout (только при изменении layout)
  useEffect(() => {
    setNodes((currentNodes) => {
      // Если узлы уже есть, сохраняем их позиции
      const nodesWithSelection = layoutedNodes.map((node) => {
        const existingNode = currentNodes.find((n) => n.id === node.id);
        return {
          ...node,
          // Сохраняем позицию из существующего узла, если она есть (пользователь мог переместить)
          position: existingNode?.position || node.position,
          data: {
            ...node.data,
            isSelected: selectedNodeId === node.id,
            isDependent: dependentNodeIds.has(node.id),
            isExpanded: expandedNodeId === node.id,
            onExpandClick: handleExpandClick,
          },
        };
      });
      return nodesWithSelection;
    });
    
    // Обновляем edges: делаем анимированными и синими, если ОБА узла выделены
    const allSelectedNodeIds = new Set([selectedNodeId, ...dependentNodeIds].filter(Boolean));
    const edgesWithAnimation = layoutedEdges.map((edge) => {
      // Edge анимируется только если ОБА узла (source и target) выделены
      const isConnected = allSelectedNodeIds.has(edge.source) && allSelectedNodeIds.has(edge.target);
      return {
        ...edge,
        animated: isConnected,
        style: {
          ...edge.style,
          stroke: isConnected ? "#3b82f6" : "#9ca3af",
        },
      };
    });
    
    setEdges(edgesWithAnimation);
  }, [layoutedNodes, layoutedEdges, setNodes, setEdges, selectedNodeId, dependentNodeIds, handleExpandClick]);

  // Обновляем только данные узлов и edges (выделение, раскрытие) без изменения позиций
  useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        // Сохраняем текущую позицию узла, чтобы не терять перемещения пользователя
        const currentNode = currentNodes.find((n) => n.id === node.id);
        return {
          ...node,
          // Сохраняем позицию из текущего узла, если она есть
          position: currentNode?.position || node.position,
          data: {
            ...node.data,
            isSelected: selectedNodeId === node.id,
            isDependent: dependentNodeIds.has(node.id),
            isExpanded: expandedNodeId === node.id,
            onExpandClick: handleExpandClick,
          },
        };
      })
    );
    
    // Обновляем edges: делаем анимированными и синими, если ОБА узла выделены
    setEdges((currentEdges) => {
      const allSelectedNodeIds = new Set([selectedNodeId, ...dependentNodeIds].filter(Boolean));
      return currentEdges.map((edge) => {
        // Edge анимируется только если ОБА узла (source и target) выделены
        const isConnected = allSelectedNodeIds.has(edge.source) && allSelectedNodeIds.has(edge.target);
        return {
          ...edge,
          animated: isConnected,
          style: {
            ...edge.style,
            stroke: isConnected ? "#3b82f6" : "#9ca3af",
          },
        };
      });
    });
  }, [selectedNodeId, dependentNodeIds, expandedNodeId, handleExpandClick, setNodes, setEdges]);

  // Обработчик клика на узел (только выделение, без раскрытия)
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    // Устанавливаем выбранный узел только для выделения
    if (selectedNodeId === node.id) {
      // Если кликнули на уже выбранный узел, снимаем выделение
      setSelectedNodeId(null);
    } else {
      setSelectedNodeId(node.id);
    }
  }, [selectedNodeId]);

  const isMobile = useMobile();

  // При открытии модального окна устанавливаем начальную таблицу и центрируем граф (только на десктопе)
  useEffect(() => {
    if (isOpen && initialTableName && datasets.length > 0 && !isMobile) {
      const initialTable = datasets.find((ds) => ds.name === initialTableName);
      if (initialTable) {
        setSelectedTable(initialTable);
        setExpandedNodeId(initialTableName);
        setSelectedNodeId(initialTableName);
      }
    } else if (!isOpen) {
      // Сбрасываем состояние при закрытии
      setSelectedTable(null);
      setExpandedNodeId(null);
      setSelectedNodeId(null);
      setHasCentered(false); // Сбрасываем флаг центрирования при закрытии
    }
  }, [isOpen, initialTableName, datasets, isMobile]);

  // Сохраняем instance ReactFlow для центрирования
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const onInit = useCallback((instance: ReactFlowInstance) => {
    setReactFlowInstance(instance);
  }, []);

  // Плавная анимация перехода к целевой таблице только при открытии окна (один раз)
  useEffect(() => {
    if (reactFlowInstance && initialTableName && nodes.length > 0 && isOpen && !hasCentered && !isMobile) {
      const targetNode = nodes.find((n) => n.id === initialTableName);
      if (targetNode && targetNode.position) {
        // Небольшая задержка для завершения рендеринга
        const timer = setTimeout(() => {
          // Центрируем таблицу посередине, но сдвигаем влево на половину ширины попапа
          // Попап справа (600px + отступы), поэтому сдвигаем центр влево
          const popupWidth = 600;
          const padding = 32; // right-4 = 16px * 2
          const totalPopupWidth = popupWidth + padding;
          
          // Центр таблицы (примерно середина узла)
          const nodeCenterX = targetNode.position.x;
          const nodeCenterY = targetNode.position.y;
          
          // Используем setCenter с плавной анимацией через viewport
          // React Flow поддерживает duration в setCenter для плавной анимации
          reactFlowInstance.setCenter(
            nodeCenterX + totalPopupWidth / 2,
            nodeCenterY,
            { zoom: 1, duration: 800 }
          );
          
          // Помечаем, что центрирование уже выполнено
          setHasCentered(true);
        }, 150);
        
        return () => clearTimeout(timer);
      }
    }
  }, [reactFlowInstance, initialTableName, nodes, isOpen, hasCentered, isMobile]);

  if (!isOpen || !datasets || datasets.length === 0) {
    return null;
  }

  const modalContent = (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm" style={{ zIndex: 99999 }}>
        <div className="bg-white rounded-lg shadow-2xl w-[90vw] h-[85vh] max-w-[1400px] flex flex-col">
          {/* Заголовок */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-border bg-neutral-light rounded-t-lg">
            <h2 className="text-xl font-semibold text-neutral-text flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              Схема данных
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-neutral-bg transition-colors text-neutral-text hover:text-primary"
              title="Закрыть"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Контент */}
        <div className="flex-1 overflow-hidden relative">
            <div className="h-full relative">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onInit={onInit}
                onPaneClick={() => {
                  setSelectedTable(null);
                  setExpandedNodeId(null);
                  setSelectedNodeId(null);
                }}
                nodeTypes={nodeTypes}
                connectionMode={ConnectionMode.Loose}
                fitView={!initialTableName}
                defaultEdgeOptions={EDGE_STYLE}
                className="bg-gradient-to-br from-neutral-bg to-white"
              >
                <Background />
                <Controls />
                <MiniMap />
              </ReactFlow>

              {/* Панель с информацией о колонках при клике */}
              {selectedTable && (
                <div className={`${isMobile ? 'fixed inset-x-0 bottom-0 top-auto max-h-[70vh]' : 'absolute right-4 top-1/2 -translate-y-1/2 w-[600px] max-h-[80vh]'} bg-white border border-neutral-border rounded-lg shadow-xl overflow-hidden flex flex-col z-10 ${isMobile ? 'rounded-t-lg rounded-b-none' : ''}`}>
                  <div className={`flex items-center justify-between ${isMobile ? 'p-3' : 'p-4'} border-b border-neutral-border bg-neutral-light flex-shrink-0`}>
                    <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-neutral-text font-mono`}>
                      {selectedTable.name}
                    </h3>
                    <button
                      onClick={() => {
                        setSelectedTable(null);
                        setExpandedNodeId(null);
                      }}
                      className={`${isMobile ? 'p-1' : 'p-1'} rounded hover:bg-neutral-bg transition-colors text-primary hover:text-primary/80`}
                      title="Скрыть документацию"
                    >
                      <ChevronDown className={isMobile ? "w-4 h-4" : "w-5 h-5"} />
                    </button>
                          </div>
                          
                  <div className={`flex-1 overflow-auto ${isMobile ? 'p-3' : 'p-4'}`}>
                    {selectedTable.description && (
                      <div className={`${isMobile ? 'mb-3 pb-3' : 'mb-4 pb-4'} border-b border-neutral-border`}>
                        <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-neutral-text ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                          Описание
                        </h4>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-neutral-text/80 leading-relaxed`}>
                          {selectedTable.description}
                        </p>
                      </div>
                    )}
                    {selectedTable.columns && selectedTable.columns.length > 0 ? (
                      <div>
                        <h4 className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-neutral-text ${isMobile ? 'mb-2' : 'mb-3'}`}>
                          Колонки ({selectedTable.columns.length})
                        </h4>
                        <div className="overflow-x-auto">
                          <table className={`min-w-full border border-neutral-border ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            <thead className="bg-neutral-light">
                              <tr>
                                <th className={`border border-neutral-border ${isMobile ? 'px-2 py-1' : 'px-3 py-2'} text-left text-neutral-text font-semibold`}>
                                  Колонка
                                </th>
                                <th className={`border border-neutral-border ${isMobile ? 'px-2 py-1' : 'px-3 py-2'} text-left text-neutral-text font-semibold`}>
                                  Тип
                                </th>
                                <th className={`border border-neutral-border ${isMobile ? 'px-2 py-1' : 'px-3 py-2'} text-left text-neutral-text font-semibold`}>
                                  Описание
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedTable.columns.map((col, colIdx) => {
                                if (!col.name) return null;
                                
                                return (
                                  <tr key={colIdx} className="odd:bg-white even:bg-neutral-bg">
                                    <td className={`border border-neutral-border ${isMobile ? 'px-2 py-1' : 'px-3 py-2'} font-mono text-neutral-text`}>
                                      <code className={`bg-neutral-light ${isMobile ? 'px-1 py-0.5' : 'px-1.5 py-0.5'} rounded ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                                        {col.name}
                                      </code>
                                    </td>
                                    <td className={`border border-neutral-border ${isMobile ? 'px-2 py-1' : 'px-3 py-2'} text-neutral-text`}>
                                      <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} bg-blue-50 text-blue-700 ${isMobile ? 'px-1 py-0.5' : 'px-2 py-0.5'} rounded`}>
                                        {col.type || "String"}
                                      </span>
                                    </td>
                                    <td className={`border border-neutral-border ${isMobile ? 'px-2 py-1' : 'px-3 py-2'} text-neutral-text/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                      {col.description || (
                                        <span className="text-neutral-text/40 italic">Нет описания</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        </div>
                      ) : (
                        <p className={`text-neutral-text/60 ${isMobile ? 'text-xs' : 'text-sm'} italic`}>Нет информации о колонках</p>
                      )}
                    </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
  );

  return createPortal(modalContent, document.body);
};
